import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { XCircle, Clock, Upload, X, Loader, ExternalLink } from 'lucide-react';
import { checkCompliance, clearComplianceResults, getAuthToken, startMultipartUpload, generatePartUrls, uploadMultipartChunk, completeMultipartUpload, createJob, getJobStatus, listJobs, getContent, scanContent } from './features/contentCompliance/contentComplianceSlice';
import { ANON_SCAN_LIMIT, asInsufficientCreditsError } from './features/contentCompliance/contentComplianceService';
import { selectIsAuthenticated, selectAuthToken } from './features/auth/authSlice';
import { showOutOfCredits, selectCreditBalance, selectEnforcementMode } from './features/billing/billingSlice';
import { TRANSCRIPT_SCAN_CREDITS, SIGNUP_BONUS_CREDITS } from './features/billing/constants';
import OutOfCreditsModal from './components/OutOfCreditsModal';
import TranscriptInput from './components/TranscriptInput';
import VideoUploadInput from './components/VideoUploadInput';
import youtubeIcon from './assets/youtube_icon.png';
import tiktokIcon from './assets/tiktok_icon.png';
import { track, scoreBand } from './analytics/analytics';
import './App.css';

// Instagram SVG icon component (wrapped in circular background like PNG icons)
const InstagramIcon = () => (
  <div className="platform-icon platform-svg-wrapper">
    <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor" className="platform-svg-icon">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  </div>
);

// Facebook SVG icon component (wrapped in circular background like PNG icons)
const FacebookIcon = () => (
  <div className="platform-icon platform-svg-wrapper">
    <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor" className="platform-svg-icon facebook-svg-icon">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  </div>
);

// Helper to map job status codes to user-friendly messages
const getStatusDisplay = (status) => {
  const statusMap = {
    'PENDING': { text: 'Starting analysis...', step: 0 },
    'DOWNLOADING': { text: 'Downloading video...', step: 1 },
    'EXTRACTING_AUDIO': { text: 'Extracting audio...', step: 2 },
    'PREPARING_TRANSCRIPTION': { text: 'Preparing transcription...', step: 2 },
    'TRANSCRIBING': { text: 'Transcribing audio...', step: 3 },
    'MERGING_TRANSCRIPTS': { text: 'Processing transcript...', step: 3 },
    'ANALYZING_VIDEO': { text: 'Analyzing video frames...', step: 4 },
    'ANALYZING': { text: 'Running compliance analysis...', step: 5 },
    'COMPLETED': { text: 'Analysis complete!', step: 6 },
    'FAILED': { text: 'Analysis failed', step: -1 },
  };
  return statusMap[status] || { text: 'Processing...', step: 0 };
};

const TOTAL_STEPS = 6;
const SUPPORTED_PLATFORMS = ['YouTube', 'TikTok', 'Instagram', 'Facebook'];

function ContentCheckerPage() {
  // Function to strip HTML tags and count words
  const stripHtmlAndCountWords = (htmlString) => {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    const textContent = doc.body.textContent || '';
    return textContent.trim().split(/\s+/).filter(Boolean).length;
  };

  const [platform, setPlatform] = useState('YouTube'); // Default to YouTube
  const [transcript, setTranscript] = useState('');
  const [scanTitle, setScanTitle] = useState(''); // Title for the scan
  const untitledCounterRef = useRef(1); // Counter for auto-generated titles
  const [showTranscriptInput, setShowTranscriptInput] = useState(false); // Toggle between transcript and video
  const [videoFile, setVideoFile] = useState(null); // State for video file
  const [isUploading, setIsUploading] = useState(false); // State for tracking upload status
  const [uploadProgress, setUploadProgress] = useState(0); // State for tracking upload progress
  const [videoUploaded, setVideoUploaded] = useState(false); // State to track if video is uploaded


  // Polling state for job status
  const pollingTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(3000); // Start with 3 seconds

  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressJobId, setProgressJobId] = useState(null);
  const [progressStatus, setProgressStatus] = useState(null);
  const [progressResult, setProgressResult] = useState(null);
  const [maxStepReached, setMaxStepReached] = useState(0); // Track highest step for progress bar

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const googleAuthToken = useSelector(selectAuthToken); // Token from Google login
  const complianceStatus = useSelector((state) => state.contentCompliance.status);
  const authToken = useSelector((state) => state.contentCompliance.authToken);
  const partUploadProgress = useSelector((state) => state.contentCompliance.partUploadProgress);
  const jobId = useSelector((state) => state.contentCompliance.jobId);
  const anonScansRemaining = useSelector((state) => state.contentCompliance.anonScansRemaining);
  const anonScanLimit = useSelector((state) => state.contentCompliance.anonScanLimit);
  const creditBalance = useSelector(selectCreditBalance);
  const enforcementMode = useSelector(selectEnforcementMode);
  // True while we're waiting for the uploaded video's duration to be extracted
  // so the scan can be priced. Shown on the button so the pause isn't silent.
  const [preparingScan, setPreparingScan] = useState(false);
  // Only surface credit costs when the backend actually consumes credits.
  // In 'off' (prod default until go-live) scans are free, so a cost line would mislead.
  const showCreditCost = isAuthenticated && enforcementMode !== 'off';
  // Anonymous users have a limited free-scan budget. Authenticated users are never limited.
  const effectiveScanLimit = anonScanLimit ?? ANON_SCAN_LIMIT;
  const scansLeft = anonScansRemaining == null ? effectiveScanLimit : anonScansRemaining;
  const anonLimitReached = !isAuthenticated && scansLeft <= 0;

  useEffect(() => {
    const selectedPlatform = location.state?.selectedPlatform;
    if (selectedPlatform && SUPPORTED_PLATFORMS.includes(selectedPlatform)) {
      setPlatform(selectedPlatform);
    }
  }, [location.state]);

  useEffect(() => {
    // Function to check token validity (a simple check for now)
    const isTokenValid = (token) => {
      // In a real application, you would decode the token and check its expiry.
      // For now, we'll just check if it exists and is not empty.
      return token && token.length > 0;
    };

    const storedToken = localStorage.getItem('cleanStudioToken');

    if (!isTokenValid(storedToken) && !authToken) {
      dispatch(getAuthToken()).unwrap()
        .then((response) => {
          const newToken = response?.data?.access_token;
          if (newToken) {
            localStorage.setItem('cleanStudioToken', newToken);
          }
        })
        .catch(error => {
          console.error("Failed to get and store auth token:", error);
        });
    } else if (isTokenValid(storedToken) && !authToken) {
      // If a valid token is in localStorage but not in Redux state, set it in Redux
      // This might happen on a page refresh
      dispatch({ type: 'contentCompliance/getAuthToken/fulfilled', payload: { token: storedToken } });
    }

    if (authToken && authToken !== 'null') {
      // Check if token exists in localStorage
      if (!localStorage.getItem('cleanStudioToken') || localStorage.getItem('cleanStudioToken') === 'null') {
        localStorage.setItem('cleanStudioToken', authToken);
      }
    }

  }, [platform, transcript, showTranscriptInput, videoFile, videoUploaded, dispatch, authToken]);

  // Track which token we last fetched jobs for
  const lastFetchedToken = useRef(null);

  // Separate useEffect to list jobs when we have a valid token
  // Triggers on both anonymous token (authToken) and Google login token (googleAuthToken)
  useEffect(() => {
    const isTokenValid = (token) => {
      return token && token.length > 0 && token !== 'null';
    };

    // Get the best available token (prefer Google auth token)
    const effectiveToken = googleAuthToken || authToken || localStorage.getItem('cleanStudioToken');

    if (isTokenValid(effectiveToken)) {
      // Only fetch if we haven't fetched for this token yet
      if (lastFetchedToken.current !== effectiveToken) {
        lastFetchedToken.current = effectiveToken;
        console.log('[History] Fetching jobs with token:', effectiveToken?.substring(0, 20) + '...');
        dispatch(listJobs({ limit: 10, sortOrder: 'desc' }));
      }
    }
  }, [authToken, googleAuthToken, dispatch]); // Run when any auth token changes

  // Polling constants
  const POLL_INITIAL_INTERVAL = 3000; // 3 seconds
  const POLL_MAX_INTERVAL = 30000; // 30 seconds max between polls
  const POLL_BACKOFF_MULTIPLIER = 1.5;
  const POLL_MAX_DURATION = 5 * 60 * 1000; // 5 minutes max total polling time
  const pollingStartTimeRef = useRef(null);

  // Stop polling function
  const stopPolling = () => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    pollIntervalRef.current = POLL_INITIAL_INTERVAL;
    pollingStartTimeRef.current = null;
  };

  // Poll for job status with exponential backoff
  const pollJobStatus = async (jobIdToPoll) => {
    if (!jobIdToPoll) return;

    // Check if we've exceeded max polling duration
    if (pollingStartTimeRef.current && Date.now() - pollingStartTimeRef.current > POLL_MAX_DURATION) {
      console.log('Polling timeout reached (5 minutes). Stopping automatic polling.');
      stopPolling();
      return;
    }

    try {
      const result = await dispatch(getJobStatus(jobIdToPoll)).unwrap();
      const status = result?.data?.status;
      const jobData = result?.data?.data;

      // Update progress modal state
      setProgressStatus(status);

      // Update max step (only moves forward)
      const currentStep = getStatusDisplay(status).step;
      if (currentStep > 0) {
        setMaxStepReached(prev => Math.max(prev, currentStep));
      }

      if (status === 'COMPLETED' || status === 'FAILED') {
        // Job finished, stop polling
        stopPolling();
        track(status === 'COMPLETED' ? 'scan_completed' : 'scan_failed', {
          platform: (platform || 'unknown').toLowerCase(),
          score_band: scoreBand(jobData?.overall_score),
          flow: 'upload',
        });
        // Store result for modal display
        if (status === 'COMPLETED' && jobData) {
          setProgressResult({
            score: jobData.overall_score,
            violations: jobData.violations || [],
            jobId: jobIdToPoll
          });
        } else if (status === 'FAILED') {
          setProgressResult({ error: jobData?.error || 'Analysis failed' });
        }
        // Refresh jobs list (reset to first page)
        dispatch(listJobs({ limit: 10, sortOrder: 'desc' }));
      } else {
        // Job still processing, schedule next poll with exponential backoff
        const nextInterval = Math.min(
          pollIntervalRef.current * POLL_BACKOFF_MULTIPLIER,
          POLL_MAX_INTERVAL
        );
        pollIntervalRef.current = nextInterval;

        pollingTimeoutRef.current = setTimeout(() => {
          pollJobStatus(jobIdToPoll);
        }, nextInterval);
      }
    } catch (error) {
      console.error('Polling error:', error);

      // On error, continue polling with backoff
      const nextInterval = Math.min(
        pollIntervalRef.current * POLL_BACKOFF_MULTIPLIER,
        POLL_MAX_INTERVAL
      );
      pollIntervalRef.current = nextInterval;

      pollingTimeoutRef.current = setTimeout(() => {
        pollJobStatus(jobIdToPoll);
      }, nextInterval);
    }
  };

  // Start polling function
  const startPolling = (jobIdToPoll) => {
    stopPolling(); // Clear any existing polling
    pollIntervalRef.current = POLL_INITIAL_INTERVAL;
    pollingStartTimeRef.current = Date.now(); // Record start time
    // Start first poll immediately
    pollJobStatus(jobIdToPoll);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  // Re-added useEffect for overall progress calculation
  useEffect(() => {
    if (videoFile && isUploading) {
      const chunkSize = 5 * 1024 * 1024; // Needs to match the chunk size in handleUploadVideo
      const totalChunks = Math.ceil(videoFile.size / chunkSize);
      let totalProgress = 0;

      for (const partNumber in partUploadProgress) {
        const part = partUploadProgress[partNumber];
        if (part.status === 'succeeded') {
          totalProgress += 100; // Each succeeded part contributes 100% of its share
        } else if (part.status === 'loading') {
          totalProgress += part.progress || 0; // Add current progress for parts still loading
        }
      }
      const overallProgress = totalChunks > 0 ? Math.round((totalProgress / totalChunks)) : 0;
      setUploadProgress(overallProgress);
    } else if (!isUploading) {
      setUploadProgress(0); // Reset progress when not uploading
    }
  }, [partUploadProgress, videoFile, isUploading]); // Dependencies: re-run when partUploadProgress or videoFile changes

  const handleClear = () => {
    setTranscript('');
    setScanTitle('');
    setVideoFile(null);
    setUploadProgress(0);
    setVideoUploaded(false);
    setIsUploading(false);
    setProgressJobId(null);
    stopPolling();
    dispatch(clearComplianceResults());
    sessionStorage.removeItem('uploadId');
  };

  const handleCloseProgressModal = () => {
    setShowProgressModal(false);
    // setProgressJobId(null);
    setProgressStatus(null);
    setProgressResult(null);
    setMaxStepReached(0); // Reset progress
    // Also clear the form for a fresh start
    setTranscript('');
    setScanTitle('');
    setVideoFile(null);
    setUploadProgress(0);
    setVideoUploaded(false);
    setIsUploading(false);
    stopPolling();
    dispatch(clearComplianceResults());
    sessionStorage.removeItem('uploadId');
  };

  const handleViewDetailedReport = () => {
    if (progressJobId) {
      navigate(`/dashboard/history?job=${progressJobId}`);
    }
  };

  const handleCheckCompliance = async () => {
    if (anonLimitReached) {
      navigate('/signup');
      return;
    }
    // Generate title if not provided
    const title = scanTitle.trim() || `Untitled ${untitledCounterRef.current}`;
    if (!scanTitle.trim()) {
      untitledCounterRef.current += 1;
    }

    if (showTranscriptInput) {
      // Show progress modal for transcript check
      setProgressJobId(null);
      setProgressStatus('ANALYZING');
      setProgressResult(null);
      setMaxStepReached(0); // Reset progress
      setShowProgressModal(true);

      try {
        const result = await dispatch(checkCompliance({ platform, transcript, title })).unwrap();
        setScanTitle('');
        dispatch(listJobs({ limit: 10, sortOrder: 'desc' }));

        // Update modal with results
        const jobData = result?.data || result;
        setProgressStatus('COMPLETED');
        setProgressResult({
          score: jobData.overall_score,
          violations: jobData.violations || [],
          jobId: jobData.job_id
        });
        if (jobData.job_id) {
          setProgressJobId(jobData.job_id);
        }
      } catch (error) {
        console.error("Compliance check failed:", error);
        const creditErr = asInsufficientCreditsError(error);
        if (creditErr) {
          // Out of credits — swap the progress modal for the top-up modal.
          setShowProgressModal(false);
          dispatch(showOutOfCredits({
            creditsRequired: creditErr.creditsRequired,
            creditsBalance: creditErr.creditsBalance,
            bandLabel: creditErr.bandLabel,
          }));
          return;
        }
        setProgressStatus('FAILED');
        if (error?.requiresSignup) {
          setProgressResult({
            error: error.message || `You've used all ${error.scanLimit ?? ANON_SCAN_LIMIT} free scans. Create a free account to keep scanning.`,
            requiresSignup: true,
          });
        } else {
          setProgressResult({ error: error?.message || 'Analysis failed' });
        }
      }
    } else if (videoUploaded && jobId) {
      try {
        await dispatch(getJobStatus(jobId)).unwrap();
        // Refresh jobs list after getting status (reset to first page)
        dispatch(listJobs({ limit: 10, sortOrder: 'desc' }));
      } catch (error) {
        console.error("Failed to get job status:", error);
      }
    } else {
      console.error("Invalid state for check compliance.");
    }
  };

  // A freshly uploaded video's duration_seconds is populated asynchronously by
  // the thumbnail Lambda. Scanning before it lands means compute_credits_for_content
  // can't price the video, so the backend deliberately fails open and the scan
  // runs free — which was giving away roughly half of all signed-up scans.
  //
  // Poll the content row until duration_seconds appears, then scan.
  //
  // Two things learned the hard way (the first version of this polled
  // GET /api/scan/cost with a 16s timeout, and shipped to dev still scanning free):
  //
  //  - Poll GET /api/content/{id}, NOT /api/scan/cost. scan/cost re-triggers the
  //    thumbnail Lambda on every call where duration is missing, so polling it
  //    spawned a duplicate ~25s extraction per poll — one upload ran the Lambda
  //    ten times. The content endpoint is a pure read.
  //
  //  - The timeout has to be generous. The thumbnail Lambda downloads the whole
  //    video before running ffprobe, so extraction time scales with file size:
  //    ~25s for a 23-minute video, longer for bigger ones. Polls are now cheap and
  //    this returns the moment duration lands, so short videos barely wait.
  //
  // Still gives up rather than blocking forever: if duration never lands we fall
  // through and accept a free scan, since refusing to scan a video the user just
  // waited to upload is worse than under-charging.
  const waitForDuration = async (contentId, timeoutMs = 90000, delayMs = 3000) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      try {
        const res = await dispatch(getContent({ contentId })).unwrap();
        if (res?.data?.duration_seconds) return res.data.duration_seconds;
      } catch (e) {
        return null; // advisory only; never let a failed read block the scan
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    return null;
  };

  const handleUploadVideo = async () => {
    if (!videoFile || !platform) return;
    if (anonLimitReached) {
      navigate('/signup');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {

      const startUploadResponse = await dispatch(startMultipartUpload({
        fileName: videoFile.name,
        fileType: videoFile.type,
        fileSize: videoFile.size,
      })).unwrap();

      const newUploadId = startUploadResponse?.data?.upload_id;
      const s3Key = startUploadResponse?.data?.file_name;
      if (!newUploadId || !s3Key) {
        throw new Error("Failed to get upload_id or file_name from startMultipartUpload.");
      }

      // Split file into chunks (5MB each)
      const chunkSize = 5 * 1024 * 1024; // 5MB
      const totalChunks = Math.ceil(videoFile.size / chunkSize);
      const partNumbers = Array.from({ length: totalChunks }, (_, i) => i + 1);

      // Call POST /generate_part_urls (use s3Key returned from start)
      const generateUrlsResponse = await dispatch(generatePartUrls({
        fileName: s3Key,
        uploadId: newUploadId,
        partNumbers: partNumbers,
      })).unwrap();

      // Check if generateUrlsResponse and urls are valid
      if (!generateUrlsResponse || !generateUrlsResponse.data || !generateUrlsResponse.data.urls) {
        throw new Error("Failed to get part URLs from generatePartUrls.");
      }
      const presignedUrls = generateUrlsResponse.data.urls;
      const eTagsArray = []; // Re-added eTagsArray

      const uploadPromises = presignedUrls.map(async (partInfo) => {
        const partNumber = partInfo.part_number;
        const url = partInfo.url;
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, videoFile.size);
        const chunk = videoFile.slice(start, end);

        const uploadResult = await dispatch(uploadMultipartChunk({
          url: url,
          chunk: chunk,
          partNumber: partNumber,
          onUploadProgress: () => {
            // Update individual part progress if needed
            // For overall progress, we'll calculate it after all parts are dispatched
          },
        })).unwrap();

        const resultETag = { ETag: uploadResult.ETag, PartNumber: uploadResult.partNumber };
        eTagsArray.push(resultETag); // Populate eTagsArray
        return uploadResult;
      });

      await Promise.all(uploadPromises);

      // After all parts are uploaded, call completeMultipartUpload
      const partsForCompletion = eTagsArray.sort((a, b) => a.PartNumber - b.PartNumber); // Use eTagsArray
      const completeUploadResponse = await dispatch(completeMultipartUpload({
        fileName: s3Key,
        uploadId: newUploadId,
        parts: partsForCompletion,
        title: scanTitle.trim() || null,
      })).unwrap();

      console.log('Multipart upload completed:', completeUploadResponse);

      // Generate title if not provided
      const jobTitle = scanTitle.trim() || `Untitled ${untitledCounterRef.current}`;
      if (!scanTitle.trim()) {
        untitledCounterRef.current += 1;
      }

      let newJobId;
      const contentId = completeUploadResponse?.data?.content_id;

      if (contentId) {
        // Wait for the duration to land so the scan is priced correctly, rather
        // than racing the thumbnail Lambda and scanning for free. Only matters
        // when credits are actually being charged.
        if (enforcementMode !== 'off') {
          setPreparingScan(true);
          await waitForDuration(contentId);
          setPreparingScan(false);
        }

        // Use scanContent to link results to content library
        console.log('Using scanContent with content_id:', contentId);
        const scanResponse = await dispatch(scanContent({
          contentId: contentId,
          platform: platform.toLowerCase(),
        })).unwrap();
        newJobId = scanResponse?.data?.job_id;
      } else {
        // Fallback to createJob if no content_id
        console.log('Fallback to createJob (no content_id)');
        const createJobResponse = await dispatch(createJob({
          fileName: videoFile.name,
          platform: platform,
          title: jobTitle,
        })).unwrap();
        newJobId = createJobResponse?.data?.job_id;
      }

      // Refresh jobs list after creating job (reset to first page)
      dispatch(listJobs({ limit: 10, sortOrder: 'desc' }));

      setVideoUploaded(true);
      setIsUploading(false);

      // Show progress modal and start polling for job status
      if (newJobId) {
        setProgressJobId(newJobId);
        setProgressStatus('PENDING');
        setProgressResult(null);
        setMaxStepReached(0); // Reset progress
        setShowProgressModal(true);
        startPolling(newJobId);
      }

    } catch (error) {
      console.error('Error in video upload workflow:', error);
      setIsUploading(false);
      setVideoUploaded(false);
      setUploadProgress(0);
      setPreparingScan(false);
      const creditErr = asInsufficientCreditsError(error);
      if (creditErr) {
        // Upload succeeded but the scan was rejected for insufficient credits.
        setShowProgressModal(false);
        dispatch(showOutOfCredits({
          creditsRequired: creditErr.creditsRequired,
          creditsBalance: creditErr.creditsBalance,
          bandLabel: creditErr.bandLabel,
        }));
        return;
      }
      if (error?.requiresSignup) {
        setProgressStatus('FAILED');
        setProgressResult({
          error: error.message || `You've used all ${error.scanLimit ?? ANON_SCAN_LIMIT} free scans. Create a free account to keep scanning.`,
          requiresSignup: true,
        });
        setShowProgressModal(true);
      } else {
        alert('Failed to start video upload workflow.');
      }
    }
  };

  return (
    <div className="App compliance-page">
      <OutOfCreditsModal />
      <main className="content-container">
        <div className="page-layout">
          <div className="main-content-area">
            {/* Banner for non-authenticated users */}
            {!isAuthenticated && (
              <section
                className="save-history-banner"
                role="button"
                tabIndex={0}
                onClick={() => navigate('/signup')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/signup');
                  }
                }}
              >
                <div className="save-history-banner-icon">
                  <Clock className="save-history-icon" />
                </div>
                <div className="save-history-banner-content">
                  <h3 className="save-history-title">
                    {`Try ${ANON_SCAN_LIMIT} Scans without login`}
                  </h3>
                  <p className="save-history-text">
                    Create a free account to get {SIGNUP_BONUS_CREDITS} free credits, saved history and multi-platform checks.
                  </p>
                  <button className="save-history-cta">
                    Create Account
                  </button>
                </div>
              </section>
            )}
        {/* Main compliance card */}
        <section className="compliance-card">

          {/* Title Input */}
          <div className="title-input-container">
            <label htmlFor="scanTitle" className="title-label">Title</label>
            <input
              id="scanTitle"
              type="text"
              value={scanTitle}
              onChange={(e) => setScanTitle(e.target.value)}
              placeholder="Enter a title (optional)"
              className="title-input"
            />
          </div>

          <div className="platform-section-label">Where will you publish?</div>
          <div className="platform-row">
            <button
              type="button"
              className={`platform-card platform-youtube ${platform === 'YouTube' ? 'selected' : 'unselected'}`}
              onClick={() => setPlatform('YouTube')}
            >
              <div className="platform-card-inner">
                <img src={youtubeIcon} alt="YouTube" className="platform-icon" />
                <span className="platform-label">YouTube</span>
              </div>
            </button>

            <button
              type="button"
              className={`platform-card platform-tiktok ${platform === 'TikTok' ? 'selected' : 'unselected'}`}
              onClick={() => setPlatform('TikTok')}
            >
              <div className="platform-card-inner">
                <img src={tiktokIcon} alt="TikTok" className="platform-icon" />
                <span className="platform-label">TikTok</span>
              </div>
            </button>

            <button
              type="button"
              className={`platform-card platform-instagram ${platform === 'Instagram' ? 'selected' : 'unselected'}`}
              onClick={() => setPlatform('Instagram')}
            >
              <div className="platform-card-inner">
                <InstagramIcon />
                <span className="platform-label">Instagram</span>
              </div>
            </button>

            <button
              type="button"
              className={`platform-card platform-facebook ${platform === 'Facebook' ? 'selected' : 'unselected'}`}
              onClick={() => setPlatform('Facebook')}
            >
              <div className="platform-card-inner">
                <FacebookIcon />
                <span className="platform-label">Facebook</span>
              </div>
            </button>
          </div>

          {/* Tabs for Transcript / Video */}
          <div className="input-tabs">
            <button
              type="button"
              className={`input-tab ${showTranscriptInput ? 'active' : ''}`}
              onClick={() => setShowTranscriptInput(true)}
            >
              Transcript
            </button>
            <button
              type="button"
              className={`input-tab ${!showTranscriptInput ? 'active' : ''}`}
              onClick={() => setShowTranscriptInput(false)}
            >
              Video
            </button>
          </div>

          {/* Input area */}
          <div className="input-area">
            {showTranscriptInput ? (
              <TranscriptInput
                transcript={transcript}
                setTranscript={setTranscript}
              />
            ) : (
              <VideoUploadInput
                videoFile={videoFile}
                setVideoFile={setVideoFile}
              />
            )}
          </div>

          {/* Footer buttons */}
          <div className="button-row">
            <button className="clear-button" type="button" onClick={handleClear}>
              Clear
            </button>

            <div className="button-row-right">
              {/* Anonymous free-scan counter */}
              {!isAuthenticated && (
                <span className="anon-scans-left">
                  {anonLimitReached
                    ? 'No free scans left'
                    : `${scansLeft} of ${effectiveScanLimit} free scans left`}
                </span>
              )}

              {/* Pre-scan credit cost hint (only when credits are enforced) */}
              {showCreditCost && (
                <span className="scan-credit-cost">
                  {showTranscriptInput
                    ? `Costs ${TRANSCRIPT_SCAN_CREDITS} credits`
                    : 'Costs 3–25 credits by length'}
                  {creditBalance != null && ` · you have ${creditBalance.toLocaleString()}`}
                </span>
              )}

              {/* Transcript check button */}
              {showTranscriptInput && (
                <button
                  className="check-button"
                  type="button"
                  onClick={handleCheckCompliance}
                  disabled={!(platform && stripHtmlAndCountWords(transcript) >= 10) || complianceStatus === 'loading' || anonLimitReached}
                >
                  {complianceStatus === 'loading' ? 'Analyzing...' : (anonLimitReached ? 'Sign up to scan' : 'Check Content')}
                </button>
              )}

              {/* Video upload button */}
              {!showTranscriptInput && !videoUploaded && (
                <button
                  className="upload-button"
                  type="button"
                  onClick={handleUploadVideo}
                  disabled={!(platform && videoFile) || isUploading || anonLimitReached}
                >
                  <Upload size={16} />
                  {preparingScan
                    ? 'Preparing scan...'
                    : isUploading ? `Uploading... ${uploadProgress}%` : (anonLimitReached ? 'Sign up to scan' : 'Upload Video')}
                </button>
              )}

              {/* Video check button (after upload) */}
              {!showTranscriptInput && videoUploaded && (
                <button
                  className="check-button"
                  type="button"
                  disabled={true}
                >
                  Analyzing...
                </button>
              )}
            </div>
          </div>

          {/* View Detailed Report Button */}
          {!showProgressModal && progressJobId && (
            <div className="view-report-container">
              <button
                className="view-report-button"
                type="button"
                onClick={handleViewDetailedReport}
              >
                <ExternalLink size={16} />
                View Detailed Report
              </button>
            </div>
          )}
        </section>

          {/* Disclaimer */}
          <div className="disclaimer">
            <p>myCleanStudio is a tool to help content creators, but cannot guarantee platform compliance.</p>
            <p>Always review each platform's specific policies before publishing your content.</p>
          </div>
          </div>{/* end main-content-area */}
        </div>{/* end page-layout */}
      </main>

      {/* Progress/Results Modal */}
      {showProgressModal && (
        <div className="scan-progress-modal-overlay" onClick={handleCloseProgressModal}>
          <div className="scan-progress-modal" onClick={(e) => e.stopPropagation()}>
            <button className="scan-progress-modal-close" onClick={handleCloseProgressModal}>
              <X size={20} />
            </button>

            {/* Still processing */}
            {progressStatus && progressStatus !== 'COMPLETED' && progressStatus !== 'FAILED' && (
              <div className="scan-progress-content">
                <Loader size={48} className="scan-progress-spinner spinning" />
                <h3 className="scan-progress-title">Analyzing Your Content</h3>
                <p className="scan-progress-platform">Platform: {platform}</p>
                <div className="scan-progress-status-box">
                  <span className="scan-progress-status">{getStatusDisplay(progressStatus).text}</span>
                  <div className="scan-progress-bar">
                    <div
                      className="scan-progress-bar-fill"
                      style={{ width: `${(maxStepReached / TOTAL_STEPS) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Completed with results */}
            {progressStatus === 'COMPLETED' && progressResult && !progressResult.error && (
              <div className="scan-result-content">
                {platform === 'Facebook' ? (
                  <FacebookIcon />
                ) : platform === 'Instagram' ? (
                  <InstagramIcon />
                ) : (
                  <img
                    src={platform === 'YouTube' ? youtubeIcon : tiktokIcon}
                    alt={platform}
                    className="scan-result-platform-icon"
                  />
                )}
                <h3 className="scan-result-title">Analysis Complete</h3>
                {(() => {
                  const s = progressResult.score;
                  const compClass = s > 70 ? 'high' : s > 40 ? 'medium' : 'low';
                  const compLabel = s > 70 ? 'Safe to publish' : s > 40 ? 'Needs review' : 'Issues found';
                  const plt = (progressResult.platform || platform || '').toLowerCase();
                  const adRisk = progressResult.adRevenueRiskLevel;
                  const fyfStat = progressResult.fyfEligibilityStatus;
                  const hasMonetization = (plt === 'youtube' && adRisk) || (plt === 'tiktok' && fyfStat);
                  const monScore = plt === 'youtube' ? progressResult.adRevenueScore : progressResult.fyfScore;
                  const monClass = plt === 'youtube'
                    ? (adRisk === 'low' ? 'high' : adRisk === 'medium' ? 'medium' : 'low')
                    : (fyfStat === 'eligible' ? 'high' : fyfStat === 'at_risk' ? 'medium' : 'low');
                  const monLabel = plt === 'youtube'
                    ? (adRisk === 'low' ? 'Advertiser-friendly' : adRisk === 'medium' ? 'Some sections limited' : 'Not ad-friendly')
                    : (fyfStat === 'eligible' ? 'FYF eligible' : fyfStat === 'at_risk' ? 'FYF at risk' : 'Not FYF eligible');
                  const monTitle = 'Earnings Compliance';
                  return (
                    <div className="scan-result-scores">
                      <div className="scan-result-score-card">
                        <span className="scan-result-score-card-label">Compliance</span>
                        <span className={`scan-result-score-card-number ${compClass}`}>{Math.round(s)}%</span>
                        <span className={`scan-result-score-card-status ${compClass}`}>{compLabel}</span>
                      </div>
                      {hasMonetization && (
                        <div className="scan-result-score-card">
                          <span className="scan-result-score-card-label">{monTitle}</span>
                          <span className={`scan-result-score-card-number ${monClass}`}>{monScore != null ? `${Math.round(monScore)}%` : '—'}</span>
                          <span className={`scan-result-score-card-status ${monClass}`}>{monLabel}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="scan-result-actions">
                  <button className="scan-result-btn secondary" onClick={handleCloseProgressModal}>
                    Close
                  </button>
                  <button className="scan-result-btn primary" onClick={handleViewDetailedReport}>
                    <ExternalLink size={16} />
                    View Report
                  </button>
                </div>
              </div>
            )}

            {/* Failed */}
            {(progressStatus === 'FAILED' || progressResult?.error) && (
              <div className="scan-result-content">
                <div className="scan-result-icon error">
                  <XCircle size={48} />
                </div>
                <h3 className="scan-result-title">
                  {progressResult?.requiresSignup ? 'Free scan limit reached' : 'Analysis Failed'}
                </h3>
                <p className="scan-result-error">{progressResult?.error || 'An error occurred during analysis'}</p>
                <div className="scan-result-actions">
                  {progressResult?.requiresSignup && (
                    <button
                      className="scan-result-btn primary"
                      onClick={() => { handleCloseProgressModal(); navigate('/signup'); }}
                    >
                      Create Account
                    </button>
                  )}
                  <button className="scan-result-btn secondary" onClick={handleCloseProgressModal}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentCheckerPage;
